// @flow

import React from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import type { StoredEntityField } from '../entities/types';
import type { StoredEntity } from '../entities/abstract/StoredEntity';
import type { SimpleEntity } from '../entities/abstract/SimpleEntity';
import ListRow from './list/ListRow';
import type { Suggestions } from './types';
import { getRandomString } from './utils';
import ShowHideComponent from './basicElements/ShowHideComponent';

export function getDefaultValueForField(field: StoredEntityField) {
    switch (field.dataType.primitive) {
        case 'str':
        case 'simple-entity':
        case 'num':
        case 'amt':
        case 'ts':
            return '';
        case 'ref':
            return [];
        case 'enum':
            return field.dataType.isArray ? '' : undefined;
        case 'bool':
            return undefined;
        default:
            return null;
    }
}

function getFlexColumnForList(valueList, valueGetter) {
    const elements = valueList.map((v) => <div key={getRandomString()}>{valueGetter(v)}</div>);

    return <div style={{ display: 'flex', flexDirection: 'column' }}>{elements}</div>;
}

function componentForPrimitiveField(field, value, valueGetter) {
    if (field.dataType.isArray) {
        return getFlexColumnForList(value, valueGetter);
    }
    return valueGetter(value);
}

export function convertFieldValuesToComponents(
    fields: [StoredEntityField],
    e: StoredEntity | SimpleEntity,
    suggestions: Suggestions,
) {
    return Object.entries(fields).map(([name, field]) => {
        const fieldValue = e.record[field.recordName];

        // If the field is optional and nil or an empty array, display 'None'
        if ((field.isOptional && !fieldValue) || (field.dataType.isArray && fieldValue.length === 0)) {
            return 'None';
        }

        switch (field.dataType.primitive) {
            case 'ts': {
                // Convert the date to a human-readable local date
                return componentForPrimitiveField(field, fieldValue, (v) => new Date(v * 1000).toLocaleString());
            }
            case 'bool': {
                // Convert the bool to Yes or No
                return componentForPrimitiveField(field, fieldValue, (v) => (v ? 'Yes' : 'No'));
            }
            case 'enum': {
                // Convert the enum value to its string representation
                return componentForPrimitiveField(field, fieldValue, (v) => field.dataType.extraData[v]);
            }
            case 'ref': {
                // Get the list of suggestions
                const sRecord = suggestions[name];
                // Get the record(s) with matching ID(s) and use the display name
                if (field.dataType.isArray) {
                    const displayName = sRecord
                        .filter((r) => fieldValue.includes(r.id))
                        .map((o) => o.displayName)
                        .join(', ');
                    return displayName;
                } else {
                    const match = sRecord ? sRecord.filter((r) => fieldValue === r.id)[0] : null;
                    const displayName = match ? match.displayName : fieldValue;
                    const model = field.dataType.extraData && field.dataType.extraData.model ? field.dataType.extraData.model() : null;
                    if (model && model.classInternalName) {
                        const infoUrl = `/${model.classInternalName}-info/${fieldValue}`;
                        return <Link to={infoUrl}>{displayName}</Link>;
                    }
                    return displayName;
                }
            }
            case 'simple-entity': {
                // Basically construct a table

                // Get the table headers from each field
                const headers = Object.values(field.dataType.extraData.model().fields).map((f) => (
                    <th key={getRandomString()}>{f.displayName}</th>
                ));

                // If it's an array, map the records to objects
                // Otherwise, just wrap the record in an array
                const model = field.dataType.extraData.model();
                const obj = field.dataType.isArray ? fieldValue.map((r) => new model(r)) : [new model(fieldValue)];

                // Get a row for each array element
                const rows = obj.map((o) => (
                    <ListRow
                        key={getRandomString()}
                        model={field.dataType.extraData.model()}
                        fields={field.dataType.extraData.model().fields}
                        entity={o}
                        rowActions={[]}
                        suggestions={{}}
                    />
                ));

                return (
                    <ShowHideComponent
                        child={
                            <Table responsive bordered>
                                <thead>
                                    <tr>{headers}</tr>
                                </thead>
                                <tbody>{rows}</tbody>
                            </Table>
                        }
                    />
                );
            }
            case 'amt':
                return componentForPrimitiveField(field, `$${fieldValue / 100}`, (v) => v);
            case 'str':
            case 'num':
            default: {
                // Just return the value as-is
                return componentForPrimitiveField(field, fieldValue, (v) => v);
            }
        }
    });
}
